import Immutable, { List, OrderedMap } from 'immutable';
import { getAppServices } from 'providers/app-services';
import { AppState, AppStateActionParams } from 'records/AppState';
import { BreadcrumbItem } from 'records/BreadcrumbItem';
import {
  ConnectedAccountId,
  ConnectedAccountList,
} from 'records/ConnectedAccount';
import { IntegrationsDrive } from 'records/IntegrationsDrive';
import { IntegrationsFolder } from 'records/IntegrationsFolder';
import { IntegrationsSelectedItem } from 'records/IntegrationsSelectedItem';
import {
  createNewFolder,
  disconnectAccount,
  exportProjectDataToIntegrations,
  getConnectedAccounts,
  getIntegrationDrives,
  getIntegrationFolder,
  importDrawingsFromDms,
} from 'services/integrationService';
import { FeatureFlag } from 'featureFlags';

export interface IntegrationActions {
  getConnectedAccounts: (
    actionParams: AppStateActionParams
  ) => Promise<AppState>;
  getIntegrationDrives: (
    actionParams: AppStateActionParams,
    providerType: string,
    connectionId: string,
    siteId?: string
  ) => Promise<AppState>;
  setIntegrationConnectedAccountId: (
    actionParams: AppStateActionParams,
    connectionId: string
  ) => AppState;
  setIntegrationsDriveId: (
    actionParams: AppStateActionParams,
    driveId: string
  ) => AppState;
  getIntegrationFolder: (
    actionParams: AppStateActionParams,
    folderParams: {
      driveId: string;
      folderId?: string;
      connectionId: string;
      providerType: string;
    }
  ) => Promise<AppState>;
  loadIntegrationsFolder: (
    actionParams: AppStateActionParams,
    driveId: string,
    folderId?: string
  ) => Promise<AppState>;
  setIntegrationsSelectedItems: (
    actionParams: AppStateActionParams,
    selectedItems: Immutable.List<IntegrationsSelectedItem>
  ) => AppState;

  setIntegrationsFolderExportData: (
    actionParams: AppStateActionParams,
    selectedFolder: IntegrationsSelectedItem | null
  ) => AppState;
  resetIntegrationsSelectedItems: (
    actionParams: AppStateActionParams
  ) => AppState;
  clearIntegrationsNavigation: (actionParams: AppStateActionParams) => AppState;

  resetIntegrationsView: (actionParams: AppStateActionParams) => AppState;
  importDrawingsFromDms: (
    actionParams: AppStateActionParams
  ) => Promise<AppState>;
  disconnectAccount: (
    actionParams: AppStateActionParams,
    connectionId: string,
    projectId: string
  ) => Promise<AppState>;

  exportProjectDataToIntegrationsFolder: (
    actionParams: AppStateActionParams,
    filesToExport: {
      drawings: boolean;
      punch: boolean;
      rfis: boolean;
      submittals: boolean;
      users: boolean;
    }
  ) => Promise<AppState>;
  setFolderSelectCurrentId: (
    actionParams: AppStateActionParams,
    folderId: string
  ) => AppState;
  createNewIntegrationsFolder: (
    actionParams: AppStateActionParams,
    folderName: string
  ) => Promise<AppState>;
}

const integrationActions: IntegrationActions = {
  getConnectedAccounts: async ({
    state,
  }: AppStateActionParams): Promise<AppState> => {
    const { featureFlagService } = getAppServices();
    const useAccountIntegration = featureFlagService.getFeature(
      FeatureFlag.ManageAccountIntegrations
    );

    // ProjectId is not required when requisitioning the drive list in the new integrations.  The backend will retrieve the BBID and use it.
    // --> explicitly use null in that scenario.
    const selectedProject = useAccountIntegration
      ? null
      : await state().get('selectedProject');

    const connectedAccounts = await getConnectedAccounts(selectedProject);

    return state().set('connectedAccounts', connectedAccounts);
  },

  getIntegrationDrives: async (
    { state }: AppStateActionParams,
    providerType: string,
    connectionId: string,
    siteId?: string
  ): Promise<AppState> => {
    const { authService, featureFlagService } = getAppServices();
    const useAccountIntegration = featureFlagService.getFeature(
      FeatureFlag.ManageAccountIntegrations
    );

    let integrationsID = null;
    if (useAccountIntegration) {
      const { userId } = authService.getCredentials();
      integrationsID = userId;
    } else {
      // Use project integration:
      const selectedProject = state().get('selectedProject');
      integrationsID = selectedProject;
    }

    const integrationDrives = await getIntegrationDrives(
      integrationsID,
      providerType,
      connectionId,
      siteId
    );

    const integrationsDrives = state().get('integrationsDrives');

    return state().set(
      'integrationsDrives',
      integrationsDrives.set(connectionId, integrationDrives)
    );
  },

  getIntegrationFolder: async (
    { state }: AppStateActionParams,
    folderParams: {
      driveId: string;
      folderId?: string;
      connectionId: string;
      providerType: string;
    }
  ): Promise<AppState> => {
    const selectedProjectId = state().selectedProject;
    const { driveId, folderId, connectionId, providerType } = folderParams;

    const integrationFolder = await getIntegrationFolder(
      connectionId,
      selectedProjectId,
      providerType,
      driveId,
      folderId
    );

    return state().set('integrationsCurrentFolder', integrationFolder);
  },

  setIntegrationConnectedAccountId: (
    { state }: AppStateActionParams,
    connectedAccountId: string
  ): AppState => {
    return state().set('integrationsConnectedAccountId', connectedAccountId);
  },

  setIntegrationsDriveId: (
    { state }: AppStateActionParams,
    driveId: string
  ): AppState => {
    return state().set('integrationsCurrentDriveId', driveId);
  },

  resetIntegrationsView: ({ state }: AppStateActionParams): AppState => {
    return state().set('integrationsCurrentFolder', new IntegrationsFolder());
  },

  loadIntegrationsFolder: async (
    { state, actions }: AppStateActionParams,
    driveId: string,
    folderId?: string
  ): Promise<AppState> => {
    const connectedAccount = state().getSelectedConnectedAccount();

    if (!connectedAccount || !driveId) {
      return state();
    }

    await actions.getIntegrationFolder({
      driveId,
      folderId,
      connectionId: connectedAccount.id,
      providerType: connectedAccount.providerType,
    });
    const folder = state().get('integrationsCurrentFolder');
    const newBreadcrumb = new BreadcrumbItem({
      driveId,
      folderId,
      label: folder.metadata.name,
      key: folderId || driveId,
    });

    const updatedState = await actions.setBreadcrumbs(newBreadcrumb);

    return updatedState.setIn(['integrationsCurrentFolder'], folder);
  },

  setIntegrationsSelectedItems: (
    { state }: AppStateActionParams,
    selectedItems: Immutable.List<IntegrationsSelectedItem>
  ): AppState => {
    return state().set('integrationsSelectedItems', selectedItems);
  },

  setIntegrationsFolderExportData: (
    { state }: AppStateActionParams,
    selectedFolder: IntegrationsSelectedItem | null
  ): AppState => {
    return state().set('integrationsFolderExportData', selectedFolder);
  },
  clearIntegrationsNavigation: ({ state }: AppStateActionParams): AppState => {
    return state()
      .set('integrationsFolderExportData', new IntegrationsSelectedItem())
      .set(
        'integrationsDrives',
        OrderedMap<ConnectedAccountId, List<IntegrationsDrive>>()
      )
      .set('integrationsCurrentFolder', new IntegrationsFolder())
      .set('integrationsCurrentDriveId', '')
      .set('integrationsConnectedAccountId', '')
      .set('selectFolderCurrentId', '');
  },

  resetIntegrationsSelectedItems: ({
    state,
  }: AppStateActionParams): AppState => {
    return state().set(
      'integrationsSelectedItems',
      Immutable.List<IntegrationsSelectedItem>()
    );
  },

  importDrawingsFromDms: async ({
    state,
    actions,
  }: AppStateActionParams): Promise<AppState> => {
    const selectedItems = state().get('integrationsSelectedItems');
    const currentProject = state().getProject().id;
    const connectedAccount = state().getSelectedConnectedAccount();

    if (!connectedAccount) {
      throw new Error('could not get connectedAccount');
    }

    await importDrawingsFromDms(
      currentProject,
      connectedAccount.providerType,
      connectedAccount.id,
      selectedItems
    );

    window.setTimeout(actions.getProcessingStatuses, 1000);

    actions.resetIntegrationsSelectedItems();

    return state().set('integrationsCurrentFolder', new IntegrationsFolder());
  },

  disconnectAccount: async (
    { state }: AppStateActionParams,
    connectionId: string,
    projectId: string
  ): Promise<AppState> => {
    const response = await disconnectAccount(connectionId, projectId);
    if (response) {
      return state().update(
        'connectedAccounts',
        (connectedAccounts: ConnectedAccountList): ConnectedAccountList => {
          return connectedAccounts.delete(
            connectedAccounts.findIndex((x) => x.id === connectionId)
          );
        }
      );
    }
    return state();
  },

  exportProjectDataToIntegrationsFolder: async (
    { state }: AppStateActionParams,
    filesToExport: {
      drawings: boolean;
      punch: boolean;
      rfis: boolean;
      submittals: boolean;
      users: boolean;
    }
  ): Promise<AppState> => {
    const exportIntoFolder = state()
      .get('integrationsSelectedItems')
      .first(undefined);
    const connectionId = state().get('integrationsConnectedAccountId');
    const bbProjectId = state().getProject().id;
    const connectedAccounts = state().get('connectedAccounts');
    const connectedAccount = connectedAccounts.find(
      (x) => x.id === connectionId
    );
    const providerType = connectedAccount
      ? connectedAccount.providerType.toUpperCase()
      : 'NONE';

    const { driveId } = exportIntoFolder!;
    const folderId = exportIntoFolder!.id;
    await exportProjectDataToIntegrations(
      driveId,
      connectionId,
      folderId,
      bbProjectId,
      providerType,
      filesToExport
    );
    return state();
  },
  createNewIntegrationsFolder: async (
    { state, actions }: AppStateActionParams,
    folderName: string
  ): Promise<AppState> => {
    const driveId = state().get('integrationsCurrentDriveId');
    const connectionId = state().get('integrationsConnectedAccountId');
    const bbProjectId = state().getProject().id;
    const parentFolderId = state().get('selectFolderCurrentId');
    const connectedAccounts = state().get('connectedAccounts');
    const connectedAccount = connectedAccounts.find(
      (x) => x.id === connectionId
    );

    const providerType = connectedAccount
      ? connectedAccount.providerType
      : 'NONE';

    const response = await createNewFolder(
      folderName,
      bbProjectId,
      connectionId,
      providerType,
      driveId,
      parentFolderId
    );

    if (response)
      await actions.getIntegrationFolder({
        driveId,
        folderId: parentFolderId,
        connectionId,
        providerType,
      });

    return state();
  },
  setFolderSelectCurrentId: (
    { state }: AppStateActionParams,
    folderId: string
  ): AppState => {
    return state().set('selectFolderCurrentId', folderId);
  },
};

export default integrationActions;
